'use client';

import React from 'react';
import { motion } from 'framer-motion';
import {
  Factory, LayoutDashboard, ShoppingCart, Package, Users, BarChart3, FileText,
  TrendingUp, AlertCircle, HardHat, Pickaxe, Map, Truck, DollarSign,
  Store, ShoppingBag, Box, Stethoscope, Activity, Heart, TestTube,
  Microscope, Dog, UserPlus, Phone, Briefcase, Award, CheckCircle,
  Clock, Crosshair, ArrowUpRight, ArrowDownRight, Globe, Scan, Monitor,
  Pill, AlertTriangle, RotateCcw, Droplets, Syringe, BellRing, Ticket
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface WidgetProps {
  accentColor?: string;
}

const defaultProfile = {
  initials: "AU",
  name: "Admin User",
  role: "Administrator",
  usage: 80
};

// ---------------------------------------------------------------------------
// HELPER COMPONENTS
// ---------------------------------------------------------------------------

const SidebarItem = ({ icon: Icon, label, active = false, color = "#2C78B9" }: { icon: any, label: string, active?: boolean, color?: string }) => (
  <div className={cn(
    "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors cursor-pointer group",
    active ? "bg-slate-100/80 text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
  )}>
    <div className={cn(
      "p-1.5 rounded-md transition-colors",
      active ? "bg-white shadow-sm" : "bg-transparent group-hover:bg-white group-hover:shadow-sm"
    )}>
      <Icon size={16} strokeWidth={active ? 2.5 : 2} style={{ color: active ? color : undefined }} />
    </div>
    <span className={cn("text-xs font-semibold", active ? "font-bold" : "font-medium")}>{label}</span>
  </div>
);

const DashboardSidebar = ({ icon: BrandIcon, brand = "Mx-ERP", items, accentColor, showProfile = true, profileData = defaultProfile }: any) => (
  <div className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col p-6 gap-8">
    <div className="flex items-center gap-3 mb-2" style={{ color: accentColor }}>
      <BrandIcon size={28} strokeWidth={2.5} />
      <span className="text-xl font-black tracking-tighter uppercase">{brand}</span>
    </div>

    <div className="space-y-6">
      <div className="space-y-1">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-3 mb-2">Main Menu</p>
        {items.map((item: any, i: number) => (
          <SidebarItem key={i} icon={item.icon} label={item.label} active={item.active} color={accentColor} />
        ))}
      </div>
    </div>

    {showProfile && profileData && (
      <div className="mt-auto p-4 bg-slate-50 rounded-2xl border border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-black" style={{ backgroundColor: accentColor }}>{profileData.initials}</div>
          <div>
            <p className="text-xs font-bold text-slate-900">{profileData.name}</p>
            <p className="text-[10px] font-medium text-slate-500">{profileData.role}</p>
          </div>
        </div>
        <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full"
            style={{ backgroundColor: accentColor }}
            initial={{ width: 0 }}
            whileInView={{ width: `${profileData.usage}%` }}
            transition={{ duration: 1.5, delay: 1 }}
          />
        </div>
        <p className="text-[9px] font-bold text-slate-400 mt-2 uppercase">Usage: {profileData.usage}% of Plan</p>
      </div>
    )}
  </div>
);

const DashboardHeader = ({ title, accentColor }: any) => (
  <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 shrink-0">
    <div className="flex items-center gap-4 flex-1">
      <div className="lg:hidden text-slate-500"><LayoutDashboard size={24} /></div>
      <div className="w-full max-w-full sm:max-w-md relative">
        <TrendingUp className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <input
          type="text"
          placeholder="Search reports, data..."
          className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-10 pr-4 text-xs font-medium focus:outline-none transition-colors"
          readOnly
        />
      </div>
    </div>
    <div className="flex items-center gap-4">
      <div className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
        <TrendingUp size={20} />
        <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border-2 border-white"></span>
      </div>
    </div>
  </header>
);

const KPICard = ({ title, value, trend, trendType, icon: Icon, color, compact = false }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={cn(
      "bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group flex-1 min-w-0",
      compact ? "p-2 sm:p-4" : "p-4"
    )}
  >
    <div className={cn("flex justify-between items-start", compact ? "mb-2" : "mb-3")}>
      <div className={cn("p-2 rounded-lg", color)}>
        <Icon size={compact ? 14 : 16} className="text-white" />
      </div>
      <div className={cn(
        "flex items-center gap-0.5 font-bold px-2 py-0.5 rounded-full",
        compact ? "text-[8px]" : "text-[9px]",
        trendType === 'neutral' ? "bg-blue-50 text-blue-600" : trendType === 'up' ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
      )}>
        {trendType === 'neutral' ? <Clock size={compact ? 8 : 10} /> : trendType === 'up' ? <ArrowUpRight size={compact ? 8 : 10} /> : <ArrowDownRight size={compact ? 8 : 10} />}
        {trend}
      </div>
    </div>
    <div className="space-y-0.5">
      <p className={cn("font-bold text-slate-400 uppercase tracking-wider", compact ? "text-[8px]" : "text-[9px]")}>{title}</p>
      <h4 className={cn("font-black text-slate-900", compact ? "text-base sm:text-lg" : "text-lg")}>{value}</h4>
    </div>
  </motion.div>
);

const AnimatedBarChart = ({ color, data }: { color: string, data: number[] }) => (
  <div className="flex items-end justify-between w-full min-h-[220px] sm:min-h-[280px] gap-2 px-2">
    {data.map((h, i) => (
      <motion.div
        key={i}
        className="w-full rounded-t-md relative group"
        style={{ backgroundColor: `${color}1A` }}
        initial={{ height: 0 }}
        whileInView={{ height: `${h}%` }}
        transition={{ duration: 0.8, delay: i * 0.1, ease: "easeOut" }}
      >
        <div className="absolute inset-0 rounded-t-md opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: color }} />
      </motion.div>
    ))}
  </div>
);

const AnimatedAreaChart = ({ color1, color2 }: { color1: string, color2: string }) => (
  <div className="w-full min-h-[220px] sm:min-h-[280px] relative">
    <svg className="w-full h-full overflow-visible" viewBox="0 0 400 150">
      <defs>
        <linearGradient id={`grad1-${color1.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color1} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color1} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`grad2-${color2.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color2} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color2} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d="M0 130 Q50 90 100 100 T200 60 T300 80 T400 30"
        fill="none" stroke={color2} strokeWidth="3" strokeLinecap="round"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 1.5, ease: "easeInOut" }}
      />
      <motion.path
        d="M0 130 Q50 90 100 100 T200 60 T300 80 T400 30 V150 H0 Z"
        fill={`url(#grad2-${color2.replace('#', '')})`}
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1.5 }}
      />

      <motion.path
        d="M0 100 Q50 120 100 80 T200 90 T300 40 T400 60"
        fill="none" stroke={color1} strokeWidth="3" strokeLinecap="round"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 1.5, ease: "easeInOut", delay: 0.2 }}
      />
      <motion.path
        d="M0 100 Q50 120 100 80 T200 90 T300 40 T400 60 V150 H0 Z"
        fill={`url(#grad1-${color1.replace('#', '')})`}
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1.5, delay: 0.2 }}
      />
    </svg>
  </div>
);

// --- Sub-components for the Manufacturing Simulation ---

const CustomChart = () => {
  return (
    <div className="w-full min-h-[110px] sm:min-h-[280px] relative">
      <svg className="w-full h-full overflow-visible" viewBox="0 0 400 150">
        <defs>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2C78B9" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#2C78B9" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 120 Q50 110 100 80 T200 60 T300 40 T400 20"
          fill="none"
          stroke="#2C78B9"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M0 120 Q50 110 100 80 T200 60 T300 40 T400 20 V150 H0 Z"
          fill="url(#chartGradient)"
        />
        {[0, 100, 200, 300, 400].map((x, i) => {
          const yValues = [120, 80, 60, 40, 20];
          return (
            <motion.circle
              key={i}
              cx={x} cy={yValues[i]} r="4"
              fill="white" stroke="#2C78B9" strokeWidth="2"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.5 + i * 0.1 }}
            />
          );
        })}
      </svg>
    </div>
  );
};

const CustomBarChart = () => {
  const bars = [60, 45, 80, 55, 90, 70, 85];
  return (
    <div className="flex items-end justify-between w-full h-[110px] sm:h-[280px] gap-2 px-2">
      {bars.map((h, i) => (
        <motion.div
          key={i}
          className="w-full bg-[#2C78B9]/10 rounded-t-md relative group"
          initial={{ height: 0 }}
          whileInView={{ height: `${h}%` }}
          transition={{ duration: 0.8, delay: i * 0.1, ease: "easeOut" }}
        >
          <div className="absolute inset-0 bg-[#2C78B9] rounded-t-md opacity-0 group-hover:opacity-100 transition-opacity" />
        </motion.div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 1. MANUFACTURING (Reference - Mx-ERP)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 1. MANUFACTURING (Reference - Mx-ERP)
// ---------------------------------------------------------------------------
const ManufacturingDashboard = ({ accentColor = "#2C78B9" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", active: true },
    { icon: ShoppingCart, label: "Trading & Sales" },
    { icon: Package, label: "Inventory" },
    { icon: Users, label: "HR & Payroll" },
    { icon: BarChart3, label: "Analytics" },
    { icon: FileText, label: "GST Reports" },
  ];

  const profileData = {
    initials: "EMP",
    name: "Employee",
    role: "Plant Manager",
    usage: 75
  };

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Factory}
        brand="Mx-ERP"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={profileData}
      />

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-2 sm:gap-3 shrink-0 order-2 lg:order-1">
            <div>
              <h3 className="text-lg sm:text-xl font-[800] text-[#0f172a] leading-tight">Operational Overview</h3>
              <p className="text-[10px] font-medium text-slate-500">Live data from Plant A and Warehouse B</p>
            </div>
            <div className="hidden sm:flex gap-2 self-start sm:self-auto">
              <button className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-600 hover:bg-slate-50 transition-colors whitespace-nowrap">Export PDF</button>
              <button className="px-3 py-1.5 bg-[#2C78B9] text-white rounded-lg text-[10px] font-black hover:shadow-lg hover:shadow-[#2C78B9]/20 transition-all whitespace-nowrap">New Work Order</button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 shrink-0 order-3 lg:order-2">
            <KPICard compact title="Trading Revenue" value="₹45.2L" trend="+12.5%" trendType="up" icon={TrendingUp} color="bg-emerald-500" />
            <KPICard compact title="Production" value="12,450" trend="+4.1%" trendType="up" icon={Factory} color="bg-[#2C78B9]" />
            <KPICard compact title="Pending Orders" value="142" trend="-2.5%" trendType="down" icon={ShoppingCart} color="bg-amber-500" />
            <KPICard compact title="Stock Alerts" value="28" trend="Requires Action" trendType="down" icon={AlertCircle} color="bg-rose-500" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 flex-1 min-h-0 order-1 lg:order-3">
            <div className="lg:col-span-2 bg-white p-3 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm flex flex-col min-h-[160px] sm:min-h-[320px] lg:min-h-0">
              <div className="flex justify-between items-center mb-2 sm:mb-4 shrink-0">
                <h4 className="font-[800] text-[#0f172a] text-[10px] uppercase tracking-tighter">Sales Growth</h4>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#2C78B9]" />
                  <span className="text-[9px] font-bold text-slate-400">Current</span>
                </div>
              </div>
              <div className="flex-1 min-h-[110px] sm:min-h-[260px] lg:min-h-0">
                <CustomChart />
              </div>
            </div>
            <div className="bg-white p-3 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm flex flex-col min-h-[160px] sm:min-h-[320px] lg:min-h-0">
              <h4 className="font-[800] text-[#0f172a] text-[10px] uppercase tracking-tighter mb-2 sm:mb-4 shrink-0">Machine Load</h4>
              <div className="flex-1 min-h-[110px] sm:min-h-[260px] lg:min-h-0 flex items-center justify-center">
                <CustomBarChart />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. CONSTRUCTION EPC
// ---------------------------------------------------------------------------
const ConstructionDashboard = ({ accentColor = "#059A8F" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Project Overview", active: true },
    { icon: Pickaxe, label: "BOQ & Planning" },
    { icon: FileText, label: "RA Billing" },
    { icon: Users, label: "Sub-contractors" },
    { icon: HardHat, label: "Site Progress" },
    { icon: BarChart3, label: "Cost Analysis" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={HardHat}
        brand="Mx-ERP"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={{ initials: "SO", name: "Site Office", role: "Project Manager", usage: 78 }}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-8 space-y-6 flex flex-col overflow-y-auto">
          <div className="shrink-0">
            <h3 className="text-2xl font-[800] text-[#0f172a]">Project Timeline</h3>
            <p className="text-sm font-medium text-slate-500">Metro Line 3 Extension - Site 4</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 shrink-0">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Budget Consumed</p>
              <p className="text-lg font-black text-[#0f172a]">₹12.4Cr <span className="text-slate-300 font-medium text-[10px]">/ ₹18.5Cr</span></p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Overall Progress</p>
              <p className="text-lg font-black" style={{ color: accentColor }}>68% Complete</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Timeline</p>
              <p className="text-lg font-black text-[#0f172a]">Day 142 <span className="text-slate-300 font-medium text-[10px]">of 210</span></p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex-1 flex flex-col min-h-[350px]">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-8">Execution Phases</h4>
            <div className="space-y-6 flex-1">
              {[
                { phase: "Foundation & Piling", pct: 100, color: "#10b981" },
                { phase: "Superstructure", pct: 68, color: accentColor },
                { phase: "MEP Works", pct: 25, color: "#f59e0b" },
                { phase: "Finishing & Handover", pct: 5, color: "#64748b" }
              ].map((item, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between text-[11px] font-bold text-slate-700 uppercase tracking-tight">
                    <span>{item.phase}</span>
                    <span>{item.pct}%</span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.pct}%` }}
                      transition={{ duration: 1.5, delay: i * 0.2 }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. TRADERS ERP
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 3. TRADERS ERP
// ---------------------------------------------------------------------------
const TradingDashboard = ({ accentColor = "#2C78B9" }: WidgetProps) => {
  const sidebarItems = [
    { icon: Package, label: "Inventory Management", active: true },
    { icon: ShoppingCart, label: "Sales & Billing" },
    { icon: ShoppingBag, label: "Procurement" },
    { icon: Truck, label: "Distribution Management" },
    { icon: DollarSign, label: "Finance & Accounting" },
    { icon: Users, label: "CRM" },
    { icon: Briefcase, label: "HR & Payroll" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={TrendingUp}
        brand="Mx-TRADING"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 space-y-4 flex flex-col overflow-y-auto">
          <div className="shrink-0">
            <h3 className="text-xl font-[800] text-[#0f172a]">Trading Overview</h3>
            <p className="text-[10px] font-medium text-slate-500">Live market and order stats</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
            <KPICard title="Purchase Orders" value="284" trend="+8%" trendType="up" icon={Package} color="bg-blue-500" />
            <KPICard title="Sales Orders" value="196" trend="+14%" trendType="up" icon={ShoppingCart} color="bg-emerald-500" />
            <KPICard title="Pending Dispatch" value="43" trend="Action Needed" trendType="down" icon={Truck} color="bg-amber-500" />
            <KPICard title="Today's Revenue" value="₹8.2L" trend="+5%" trendType="up" icon={DollarSign} color="bg-indigo-500" />
          </div>
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm flex-1 min-h-[280px] sm:min-h-[320px] lg:min-h-0 flex flex-col">
            <h4 className="font-[800] text-[#0f172a] text-[10px] uppercase tracking-tighter mb-4 shrink-0">Buy vs Sell Volume</h4>
            <div className="flex-1 min-h-[220px] sm:min-h-[260px] lg:min-h-0">
              <AnimatedAreaChart color1="#f59e0b" color2={accentColor} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 4. DISTRIBUTORS ERP
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 4. DISTRIBUTORS ERP
// ---------------------------------------------------------------------------
const DistributorDashboard = ({ accentColor = "#2C78B9" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Distribution Map", active: true },
    { icon: Truck, label: "Route Planning" },
    { icon: Store, label: "Retailer Claims" },
    { icon: Package, label: "Stock Transit" },
    { icon: Map, label: "Geographical Analysis" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Map}
        brand="NETWORK"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 space-y-4 flex flex-col overflow-y-auto">
          <div>
            <h3 className="text-2xl font-[800] text-[#0f172a]">Distribution Network</h3>
            <p className="text-sm font-medium text-slate-500">Live transit and route tracking</p>
          </div>
          <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-[400px]">
            <div className="flex-1 bg-white rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden flex items-center justify-center p-6">
              <svg viewBox="0 0 100 100" className="w-full h-full opacity-20">
                <path d="M20,20 L80,20 L80,80 L20,80 Z" fill="none" stroke="#000" strokeWidth="1" />
              </svg>
              <div className="absolute inset-0">
                <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} className="absolute top-[30%] left-[40%] w-4 h-4 bg-blue-500 rounded-full" />
                <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, delay: 0.5, repeat: Infinity }} className="absolute top-[60%] left-[30%] w-4 h-4 bg-emerald-500 rounded-full" />
                <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, delay: 1, repeat: Infinity }} className="absolute top-[50%] left-[70%] w-4 h-4 bg-amber-500 rounded-full" />

                <svg className="w-full h-full absolute inset-0">
                  <motion.path d="M 40% 30% L 30% 60%" stroke={accentColor} strokeWidth="2" strokeDasharray="5,5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, repeat: Infinity }} />
                  <motion.path d="M 40% 30% L 70% 50%" stroke={accentColor} strokeWidth="2" strokeDasharray="5,5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, repeat: Infinity, delay: 1 }} />
                </svg>
              </div>
            </div>
            <div className="w-full lg:w-72 space-y-4">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter">Active Routes</h4>
              {[
                { r: "Delhi → Mumbai", u: "1,240", p: 75 },
                { r: "Mumbai → Pune", u: "540", p: 40 },
                { r: "Chennai → Bangalore", u: "320", p: 90 }
              ].map((rt, i) => (
                <div key={i} className="bg-white p-4 rounded-xl border border-slate-200">
                  <p className="text-sm font-bold text-slate-800">{rt.r}</p>
                  <p className="text-xs text-slate-500 mb-2">{rt.u} Units</p>
                  <div className="w-full h-2 bg-slate-100 rounded-full">
                    <motion.div className="h-full bg-blue-500 rounded-full" initial={{ width: 0 }} whileInView={{ width: `${rt.p}%` }} />
                  </div>
                </div>
              ))}
              <div className="mt-8 p-4 bg-slate-800 rounded-xl text-white">
                <p className="text-xs text-slate-400 uppercase font-bold">Total In Transit</p>
                <p className="text-2xl font-black">4,200 Units</p>
                <p className="text-xs text-emerald-400">Across 18 Active Routes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 5. RETAIL ERP
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 5. RETAIL ERP
// ---------------------------------------------------------------------------
const RetailDashboard = ({ accentColor = "#7F77DD" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Store Dashboard", active: true },
    { icon: Box, label: "Inventory" },
    { icon: Users, label: "Loyalty" },
    { icon: ShoppingBag, label: "Sales Analysis" },
    { icon: FileText, label: "GST Billing" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Store}
        brand="RETAIL POS"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 space-y-4 flex flex-col overflow-y-auto">
          <div className="flex justify-between items-center bg-white p-6 rounded-3xl border border-slate-200 shadow-sm shrink-0">
            <div>
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Today's Sales</p>
              <div className="flex items-baseline gap-4">
                <motion.h2
                  className="text-4xl font-black text-slate-900"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                >
                  ₹2,84,000
                </motion.h2>
                <span className="text-emerald-500 font-bold text-sm bg-emerald-50 px-2 py-1 rounded-full">+18% vs Yesterday</span>
              </div>
              <p className="text-sm font-medium text-slate-500 mt-1">284 transactions today</p>
            </div>
            <div className="p-4 bg-[#7F77DD]/10 rounded-2xl text-[#7F77DD]">
              <Store size={40} />
            </div>
          </div>
          <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-[300px]">
            <div className="flex-1 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Hourly Sales</h4>
              <div className="h-48 flex items-end justify-between px-2">
                {[20, 30, 40, 60, 80, 100, 70, 50, 90, 85, 60, 40].map((h, i) => (
                  <motion.div key={i} className="w-6 rounded-t-md" style={{ backgroundColor: accentColor }} initial={{ height: 0 }} whileInView={{ height: `${h}%` }} transition={{ delay: i * 0.05 }} />
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-400 font-bold px-2">
                <span>8 AM</span><span>1 PM</span><span>8 PM</span>
              </div>
            </div>
            <div className="w-full lg:w-64 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Top Items</h4>
              <div className="space-y-4">
                {[
                  { n: "Premium Denim", u: 42, c: "bg-blue-100 text-blue-600" },
                  { n: "Cotton T-Shirt", u: 86, c: "bg-emerald-100 text-emerald-600" },
                  { n: "Summer Dress", u: 24, c: "bg-pink-100 text-pink-600" }
                ].map((it, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", it.c)}>
                      <ShoppingBag size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{it.n}</p>
                      <p className="text-xs text-slate-500">{it.u} units sold</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 6. BUYING HOUSE ERP
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 6. BUYING HOUSE ERP
// ---------------------------------------------------------------------------
const BuyingHouseDashboard = ({ accentColor = "#059A8F" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Order Tracking", active: true },
    { icon: FileText, label: "Tech Packs" },
    { icon: Factory, label: "Factory Allocation" },
    { icon: CheckCircle, label: "Inspections" },
    { icon: Package, label: "Sourcing Master" },
    { icon: Users, label: "Buyer Portal" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Package}
        brand="BUYING HOUSE"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 space-y-4 flex flex-col overflow-auto">
          <div>
            <h3 className="text-2xl font-[800] text-[#0f172a]">Order Pipeline</h3>
            <p className="text-sm font-medium text-slate-500">Live status of buyer POs</p>
          </div>
          <div className="bg-white p-4 sm:p-8 rounded-3xl border border-slate-200 shadow-sm relative min-h-[160px] flex items-center overflow-hidden">
            <div className="flex justify-between items-center w-full relative z-10">
              {[
                { s: "Sourcing", n: 12 }, { s: "Sampling", n: 8 },
                { s: "Production", n: 24 }, { s: "Shipment", n: 5 }
              ].map((st, i) => (
                <div key={i} className="flex flex-col items-center flex-1 min-w-0">
                  <motion.div initial={{ scale: 0 }} whileInView={{ scale: 1 }} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-white shadow-lg flex items-center justify-center mb-1 sm:mb-3 text-white" style={{ backgroundColor: accentColor }}>
                    <span className="text-sm sm:text-xl font-black">{st.n}</span>
                  </motion.div>
                  <span className="text-[9px] sm:text-sm font-bold text-center leading-tight text-slate-600">{st.s}</span>
                </div>
              ))}
            </div>
            <div className="hidden sm:block absolute top-10 left-[10%] right-[10%] h-1 border-t-2 border-dashed border-slate-300 z-0" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { v: "Alpha Garments", q: "12,000", s: "In Production", c: "text-amber-500" },
              { v: "Beta Textiles", q: "5,000", s: "QC Passed", c: "text-emerald-500" },
              { v: "Gamma Apparel", q: "8,500", s: "Sampling", c: "text-blue-500" }
            ].map((ord, i) => (
              <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-2xl">🏭</span>
                  <span className={cn("text-xs font-bold px-2 py-1 rounded-full bg-slate-50", ord.c)}>{ord.s}</span>
                </div>
                <h4 className="font-bold text-slate-800">{ord.v}</h4>
                <p className="text-sm text-slate-500 mt-1">{ord.q} pcs</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 7. E-COMMERCE ERP
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 7. E-COMMERCE ERP
// ---------------------------------------------------------------------------
const EcommerceDashboard = ({ accentColor = "#2C78B9" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Analytics", active: true },
    { icon: Package, label: "Orders sync" },
    { icon: Truck, label: "Shipping" },
    { icon: Globe, label: "Storefronts" },
    { icon: BarChart3, label: "Conversion" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={ShoppingCart}
        brand="ECOMMERCE"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 flex flex-col lg:flex-row gap-6 overflow-y-auto">
          <div className="flex-1 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col min-h-[400px]">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-8">Sales Funnel</h4>
            <div className="flex-1 flex flex-col justify-center space-y-4">
              {[
                { l: "Visitors", n: "12,450", w: "100%", c: "#bae6fd" },
                { l: "Add to Cart", n: "3,280", w: "70%", c: "#7dd3fc" },
                { l: "Checkout", n: "1,640", w: "45%", c: "#38bdf8" },
                { l: "Orders", n: "892", w: "25%", c: accentColor }
              ].map((f, i) => (
                <div key={i} className="flex flex-col items-center w-full">
                  <div className="flex justify-between w-full mb-1 px-2 text-sm font-bold text-slate-700">
                    <span>{f.l}</span><span>{f.n}</span>
                  </div>
                  <motion.div initial={{ width: 0 }} whileInView={{ width: f.w }} transition={{ duration: 1 }} className="h-8 rounded-full w-full sm:max-w-sm" style={{ backgroundColor: f.c }} />
                </div>
              ))}
            </div>
          </div>
          <div className="w-full lg:w-64 space-y-4">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Channels</h4>
            {[
              { n: "Shopify", r: "₹4.2L", p: 80, c: "#10b981" },
              { n: "Amazon", r: "₹3.8L", p: 70, c: "#f59e0b" },
              { n: "Website", r: "₹1.2L", p: 30, c: accentColor }
            ].map((ch, i) => (
              <div key={i} className="bg-white p-4 rounded-xl border border-slate-200">
                <div className="flex justify-between text-sm font-bold text-slate-800 mb-1">
                  <span>{ch.n}</span><span>{ch.r}</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${ch.p}%` }} className="h-full rounded-full" style={{ backgroundColor: ch.c }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 8. HOSPITAL HMIS
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 8. HOSPITAL HMIS
// ---------------------------------------------------------------------------
const HospitalDashboard = ({ accentColor = "#059A8F" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Hospital Command", active: true },
    { icon: Users, label: "OPD Desk" },
    { icon: Activity, label: "IPD & Wards" },
    { icon: TestTube, label: "Diagnostics" },
    { icon: Heart, label: "Patient Care" },
    { icon: FileText, label: "Billing & Insurance" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Heart}
        brand="ELECTRA-HIS"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={{ initials: "HA", name: "Hospital Admin", role: "Medical Superintendent", usage: 84 }}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3 sm:gap-4 shrink-0">
            <KPICard compact title="OPD Today" value="284" trend="High Volume" trendType="up" icon={Users} color="bg-blue-500" />
            <KPICard compact title="Operations" value="12" trend="Scheduled" trendType="neutral" icon={Activity} color="bg-emerald-500" />
            <KPICard compact title="Lab Reports" value="156" trend="Pending: 12" trendType="down" icon={TestTube} color="bg-purple-500" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 flex-1 min-h-0">
            <div className="bg-white p-3 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm flex flex-col min-h-[260px] sm:min-h-[320px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-3 sm:mb-6 shrink-0">Occupancy Gauges</h4>
              <div className="flex flex-row items-center justify-center gap-6 sm:gap-10 shrink-0 py-1">
                {[
                  { value: 78, label: "BEDS", color: accentColor },
                  { value: 92, label: "ICU", color: "#f43f5e", delay: 0.2 }
                ].map((gauge, i) => (
                  <div key={i} className="relative w-24 h-24 sm:w-32 sm:h-32 shrink-0">
                    <svg viewBox="0 0 160 160" className="w-full h-full transform -rotate-90">
                      <circle cx="80" cy="80" r="70" fill="none" stroke="#f1f5f9" strokeWidth="12" />
                      <motion.circle cx="80" cy="80" r="70" fill="none" stroke={gauge.color} strokeWidth="12" strokeDasharray="440" initial={{ strokeDashoffset: 440 }} whileInView={{ strokeDashoffset: 440 - (440 * (gauge.value / 100)) }} transition={{ duration: 1.5, delay: gauge.delay }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl sm:text-2xl font-black text-slate-800">{gauge.value}%</span>
                      <span className="text-[9px] sm:text-xs font-bold text-slate-400">{gauge.label}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 sm:mt-4 space-y-2.5 border-t border-slate-100 pt-3 sm:pt-4">
                {[
                  { label: "General Beds", count: "156 / 200", width: "78%", color: accentColor },
                  { label: "ICU", count: "23 / 25", width: "92%", color: "#f43f5e" }
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex justify-between text-[11px] sm:text-xs font-bold text-slate-600 mb-1">
                      <span>{row.label}</span><span className="tabular-nums">{row.count}</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: row.width, backgroundColor: row.color }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                <p className="text-[11px] sm:text-xs font-bold text-rose-600 leading-snug">ICU at 92% — only 2 beds left · Consider step-down transfers</p>
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm flex flex-col min-h-[280px] sm:min-h-[320px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4 sm:mb-6 shrink-0">Live Feed</h4>
              <div className="flex-1 divide-y divide-slate-100 overflow-y-auto">
                {[
                  { n: "Dr. Sharma", d: "OPD Room 3 · 2m ago", s: "On Round", pill: "bg-emerald-50 text-emerald-600", init: "SS" },
                  { n: "Dr. Gupta", d: "OT-2 · 15m ago", s: "In Surgery", pill: "bg-rose-50 text-rose-600", init: "DG" },
                  { n: "Dr. Verma", d: "Emergency · 5m ago", s: "Triage", pill: "bg-amber-50 text-amber-600", init: "SV" },
                  { n: "Billing Desk", d: "TPA Claim #4821 approved · 22m ago", s: "Approved", pill: "bg-blue-50 text-blue-600", init: "BD" },
                  { n: "Pharmacy", d: "Amoxicillin below reorder level · 31m ago", s: "Stock Alert", pill: "bg-amber-50 text-amber-600", init: "PH" }
                ].map((row) => (
                  <div key={row.n} className="flex items-center gap-3 py-2.5">
                    <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-600 shrink-0">{row.init}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-slate-800 leading-tight">{row.n}</p>
                      <p className="text-[11px] text-slate-500 truncate">{row.d}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap shrink-0 ${row.pill}`}>{row.s}</span>
                  </div>
                ))}
              </div>
              <div className="pt-3 mt-1 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs font-bold" style={{ color: accentColor }}>View full activity log →</span>
                <span className="text-[10px] font-semibold text-slate-400">Updated just now</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 8b. QUEUE MANAGEMENT (QMS)
// ---------------------------------------------------------------------------
const QueueDashboard = ({ accentColor = "#7C3AED" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Queue Command", active: true },
    { icon: Users, label: "Tokens" },
    { icon: Monitor, label: "Display Screens" },
    { icon: Clock, label: "Slot Calendar" },
    { icon: BellRing, label: "Reminders" },
    { icon: BarChart3, label: "Queue Analytics" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Ticket}
        brand="QMS"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={{ initials: "FO", name: "Front Office", role: "OPD Manager", usage: 82 }}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3 sm:gap-4 shrink-0">
            <KPICard compact title="Tokens Today" value="342" trend="High Footfall" trendType="up" icon={Users} color="bg-violet-500" />
            <KPICard compact title="Avg Wait" value="12 min" trend="-60% vs Manual" trendType="down" icon={Clock} color="bg-emerald-500" />
            <KPICard compact title="No-Shows" value="8%" trend="Halved" trendType="down" icon={BellRing} color="bg-amber-500" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 flex-1 min-h-0">
            <div className="bg-[#0F172A] p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-sm flex flex-col min-h-[300px] sm:min-h-[340px]">
              <div className="flex items-center justify-between mb-4 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Live OPD Queue</span>
                </div>
                <span className="rounded-lg border border-white/20 bg-white/10 px-2 py-1 text-[9px] font-bold text-white">6 counters open</span>
              </div>
              <div className="rounded-2xl border border-emerald-300/25 bg-emerald-400/10 p-4 mb-4 shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[9px] font-black uppercase tracking-[0.22em] text-emerald-300">Now Serving · Counter 3</p>
                    <p className="text-4xl sm:text-5xl font-black tracking-tight text-white mt-1">A-045</p>
                    <p className="text-[11px] font-semibold text-emerald-100/90 mt-1">Please proceed for consultation</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-200">Up Next</p>
                    <p className="text-2xl font-black text-amber-300 mt-1">A-041</p>
                    <p className="text-[10px] font-semibold text-slate-200 mt-0.5">Get ready, please</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2.5">
                {[
                  { token: "A-041", label: "Next", pill: "bg-amber-400/15 text-amber-300", bar: "bg-amber-400", width: "38%" },
                  { token: "A-042", label: "Waiting", pill: "bg-slate-400/15 text-slate-300", bar: "bg-slate-500", width: "14%" },
                  { token: "A-043", label: "Waiting", pill: "bg-slate-400/15 text-slate-300", bar: "bg-slate-500", width: "14%" },
                ].map((row) => (
                  <div key={row.token} className="flex items-center gap-3">
                    <span className="w-12 shrink-0 text-xs font-black tracking-wide text-white">{row.token}</span>
                    <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-white/15">
                      <motion.div
                        className={`h-full rounded-full ${row.bar}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: row.width }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2 }}
                      />
                    </div>
                    <span className={`shrink-0 rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${row.pill}`}>{row.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm flex flex-col min-h-[300px] sm:min-h-[340px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4 sm:mb-6 shrink-0">Counter Load</h4>
              <div className="flex-1 space-y-4 sm:space-y-5">
                {[
                  { counter: "Counter 1 · OPD", load: "82%", value: 82, status: "Busy", statusColor: "text-rose-500" },
                  { counter: "Counter 2 · OPD", load: "64%", value: 64, status: "Serving", statusColor: "text-emerald-500" },
                  { counter: "Counter 3 · Priority", load: "45%", value: 45, status: "Serving", statusColor: "text-emerald-500" },
                  { counter: "Counter 4 · Lab", load: "30%", value: 30, status: "Free", statusColor: "text-slate-400" },
                ].map((c, i) => (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs font-bold text-slate-700">{c.counter}</span>
                      <span className={`text-[10px] font-black uppercase ${c.statusColor}`}>{c.status} · {c.load}</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: accentColor }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${c.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: i * 0.1 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 9. LAB MANAGEMENT (LIMS)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 9. LAB MANAGEMENT (LIMS)
// ---------------------------------------------------------------------------
const LabDashboard = ({ accentColor = "#7F77DD" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Lab Overview", active: true },
    { icon: TestTube, label: "Samples" },
    { icon: FileText, label: "Reports" },
    { icon: Microscope, label: "Analyzer Link" },
    { icon: BarChart3, label: "TAT Analytics" },
    { icon: Users, label: "B2B Portal" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Microscope}
        brand="LIMS"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-4 sm:p-6 flex flex-col space-y-3 sm:space-y-4 overflow-auto">
          <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm shrink-0">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4 sm:mb-8">Sample Pipeline</h4>
            <div className="flex flex-row items-center justify-between gap-2 sm:gap-4 relative">
              {[
                { s: "Received", c: 45 }, { s: "Processing", c: 82 },
                { s: "Analysis", c: 18 }, { s: "Report Ready", c: 11 }
              ].map((st, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center min-w-0">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold mb-1 sm:mb-2 shadow-md text-xs sm:text-base shrink-0" style={{ backgroundColor: accentColor }}>
                    {st.c}
                  </div>
                  <span className="text-[9px] sm:text-sm font-bold text-slate-600 text-center leading-tight truncate w-full">{st.s}</span>
                </div>
              ))}
              <div className="absolute top-4 left-[8%] right-[8%] h-1 bg-slate-100 -z-0 hidden sm:block">
                <motion.div initial={{ width: 0 }} whileInView={{ width: '100%' }} transition={{ duration: 2 }} className="h-full" style={{ backgroundColor: accentColor }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 shrink-0">
            <KPICard title="Samples Today" value="156" trend="+12" trendType="up" icon={TestTube} color="bg-purple-500" />
            <KPICard title="On-time Reports" value="94%" trend="Target 95%" trendType="down" icon={CheckCircle} color="bg-emerald-500" />
            <KPICard title="Avg TAT" value="4.2 Hrs" trend="-0.5 Hrs" trendType="up" icon={Clock} color="bg-blue-500" />
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            {[
              { n: "Hematology", c: 48 }, { n: "Biochemistry", c: 62 },
              { n: "Microbiology", c: 28 }, { n: "Other", c: 18 }
            ].map((cat, i) => (
              <div key={i} className="flex-1 min-w-[150px] bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center shadow-sm">
                <span className="text-sm font-bold text-slate-700">{cat.n}</span>
                <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-black" style={{ color: accentColor }}>{cat.c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 10. RADIOLOGY INFORMATION SYSTEM (RIS)
// ---------------------------------------------------------------------------
const RisDashboard = ({ accentColor = "#0F8B9C" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Radiology Overview", active: true },
    { icon: Scan, label: "Study Pipeline" },
    { icon: Monitor, label: "Modality Worklist" },
    { icon: FileText, label: "Reporting" },
    { icon: Clock, label: "TAT Analytics" },
    { icon: BarChart3, label: "Dose & Compliance" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Scan}
        brand="RIS"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={{ initials: "RAD", name: "Radiologist", role: "Head of Radiology", usage: 75 }}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-4 sm:p-6 flex flex-col space-y-3 sm:space-y-4 overflow-auto">
          <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm shrink-0">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4 sm:mb-8">Study Pipeline</h4>
            <div className="flex flex-row items-center justify-between gap-2 sm:gap-4 relative">
              {[
                { s: "Ordered", c: 38 }, { s: "In Progress", c: 21 },
                { s: "Awaiting Report", c: 34 }, { s: "Critical", c: 3 }
              ].map((st, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center min-w-0">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold mb-1 sm:mb-2 shadow-md text-xs sm:text-base shrink-0" style={{ backgroundColor: st.s === "Critical" ? "#f59e0b" : accentColor }}>
                    {st.c}
                  </div>
                  <span className="text-[9px] sm:text-sm font-bold text-slate-600 text-center leading-tight truncate w-full">{st.s}</span>
                </div>
              ))}
              <div className="absolute top-4 left-[8%] right-[8%] h-1 bg-slate-100 -z-0 hidden sm:block">
                <motion.div initial={{ width: 0 }} whileInView={{ width: '100%' }} transition={{ duration: 2 }} className="h-full" style={{ backgroundColor: accentColor }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 shrink-0">
            <KPICard title="Studies Today" value="160" trend="+18" trendType="up" icon={Scan} color="bg-teal-500" />
            <KPICard title="On-time Reports" value="93%" trend="Target 95%" trendType="down" icon={CheckCircle} color="bg-emerald-500" />
            <KPICard title="Avg TAT" value="3.6 Hrs" trend="-0.4 Hrs" trendType="up" icon={Clock} color="bg-blue-500" />
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            {[
              { n: "X-Ray", c: 64 }, { n: "CT", c: 31 },
              { n: "MRI", c: 19 }, { n: "Ultrasound", c: 46 }
            ].map((cat, i) => (
              <div key={i} className="flex-1 min-w-[150px] bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center shadow-sm">
                <span className="text-sm font-bold text-slate-700">{cat.n}</span>
                <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-black" style={{ color: accentColor }}>{cat.c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 11. PHARMACY MANAGEMENT (PMS)
// ---------------------------------------------------------------------------
const PharmacyDashboard = ({ accentColor = "#0B6BCB" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Pharmacy Overview", active: true },
    { icon: FileText, label: "Prescriptions" },
    { icon: Package, label: "Stock & Batches" },
    { icon: AlertTriangle, label: "Expiry Alerts" },
    { icon: ShoppingCart, label: "Purchase Orders" },
    { icon: RotateCcw, label: "Returns" },
    { icon: BarChart3, label: "Reports" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Pill}
        brand="PMS"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={{ initials: "PHR", name: "Pharmacist", role: "Chief Pharmacist", usage: 82 }}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-4 sm:p-6 flex flex-col space-y-3 sm:space-y-4 overflow-auto">
          <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm shrink-0">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4 sm:mb-8">Prescription Pipeline</h4>
            <div className="flex flex-row items-center justify-between gap-2 sm:gap-4 relative">
              {[
                { s: "Prescription", c: 248 }, { s: "Verification", c: 64 },
                { s: "Dispensing", c: 212 }, { s: "Billed", c: 198 }
              ].map((st, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center min-w-0">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold mb-1 sm:mb-2 shadow-md text-xs sm:text-base shrink-0" style={{ backgroundColor: accentColor }}>
                    {st.c}
                  </div>
                  <span className="text-[9px] sm:text-sm font-bold text-slate-600 text-center leading-tight truncate w-full">{st.s}</span>
                </div>
              ))}
              <div className="absolute top-4 left-[8%] right-[8%] h-1 bg-slate-100 -z-0 hidden sm:block">
                <motion.div initial={{ width: 0 }} whileInView={{ width: '100%' }} transition={{ duration: 2 }} className="h-full" style={{ backgroundColor: accentColor }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 shrink-0">
            <KPICard title="Prescriptions Today" value="248" trend="+32" trendType="up" icon={FileText} color="bg-blue-500" />
            <KPICard title="Dispensed On-time" value="96%" trend="Target 98%" trendType="down" icon={CheckCircle} color="bg-emerald-500" />
            <KPICard title="Low Stock Items" value="14" trend="Reorder" trendType="down" icon={Package} color="bg-amber-500" />
            <KPICard title="Expiring in 30 Days" value="9" trend="Alert" trendType="down" icon={AlertTriangle} color="bg-rose-500" />
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            {[
              { n: "Antibiotics", c: 70 }, { n: "Analgesics", c: 55 },
              { n: "Cardiac", c: 38 }, { n: "Surgical / Others", c: 26 }
            ].map((cat, i) => (
              <div key={i} className="flex-1 min-w-[150px] bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center shadow-sm">
                <span className="text-sm font-bold text-slate-700">{cat.n}</span>
                <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-black" style={{ color: accentColor }}>{cat.c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 12. BLOOD BANK MANAGEMENT
// ---------------------------------------------------------------------------
const BloodBankDashboard = ({ accentColor = "#C8102E" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Bank Overview", active: true },
    { icon: Users, label: "Donors" },
    { icon: Syringe, label: "Collection" },
    { icon: TestTube, label: "TTI Testing" },
    { icon: Package, label: "Components" },
    { icon: FileText, label: "Cross-match & Issue" },
    { icon: AlertTriangle, label: "Expiry Alerts" },
    { icon: BarChart3, label: "Reports" },
  ];

  const groups = [
    { n: "A+", c: 42, low: false }, { n: "A−", c: 8, low: false },
    { n: "B+", c: 55, low: false }, { n: "B−", c: 3, low: true },
    { n: "O+", c: 61, low: false }, { n: "O−", c: 9, low: false },
    { n: "AB+", c: 17, low: false }, { n: "AB−", c: 2, low: true },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Droplets}
        brand="BLOOD BANK"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
        profileData={{ initials: "BBO", name: "Blood Bank Officer", role: "Medical Officer In-charge", usage: 78 }}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-4 sm:p-6 flex flex-col space-y-3 sm:space-y-4 overflow-auto">
          <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-sm shrink-0">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4 sm:mb-8">Unit Pipeline</h4>
            <div className="flex flex-row items-center justify-between gap-2 sm:gap-4 relative">
              {[
                { s: "Registered", c: 56 }, { s: "Collected", c: 48 },
                { s: "Tested Clear", c: 45 }, { s: "Issued", c: 39 }
              ].map((st, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center min-w-0">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold mb-1 sm:mb-2 shadow-md text-xs sm:text-base shrink-0" style={{ backgroundColor: accentColor }}>
                    {st.c}
                  </div>
                  <span className="text-[9px] sm:text-sm font-bold text-slate-600 text-center leading-tight truncate w-full">{st.s}</span>
                </div>
              ))}
              <div className="absolute top-4 left-[8%] right-[8%] h-1 bg-slate-100 -z-0 hidden sm:block">
                <motion.div initial={{ width: 0 }} whileInView={{ width: '100%' }} transition={{ duration: 2 }} className="h-full" style={{ backgroundColor: accentColor }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 shrink-0">
            <KPICard title="Units In Stock" value="197" trend="+12" trendType="up" icon={Droplets} color="bg-rose-500" />
            <KPICard title="Critical Groups Low" value="2" trend="B−, AB−" trendType="down" icon={AlertTriangle} color="bg-amber-500" />
            <KPICard title="TTI Reactive" value="3" trend="Quarantined" trendType="down" icon={TestTube} color="bg-purple-500" />
            <KPICard title="Expiring in 7 Days" value="6" trend="Alert" trendType="down" icon={Clock} color="bg-blue-500" />
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm shrink-0">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Live Group Stock</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {groups.map((g, i) => (
                <div key={i} className="p-3 rounded-xl border flex justify-between items-center shadow-sm" style={{ borderColor: g.low ? accentColor : undefined, backgroundColor: g.low ? `${accentColor}0d` : undefined }}>
                  <span className="text-sm font-black" style={{ color: accentColor }}>{g.n}</span>
                  <span className="text-xs font-bold text-slate-600">{g.c} units{g.low ? " ⚠" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 13. VETERINARY LAB
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 13. VETERINARY LAB
// ---------------------------------------------------------------------------
const VetDashboard = ({ accentColor = "#f59e0b" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Diagnostics", active: true },
    { icon: FileText, label: "Certificates" },
    { icon: Activity, label: "Surveillance" },
    { icon: Dog, label: "Species Master" },
    { icon: Microscope, label: "Equine Tests" },
    { icon: BarChart3, label: "Analytics" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Dog}
        brand="VET LAB"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 flex flex-col lg:flex-row gap-4 overflow-y-auto">
          <div className="w-full lg:w-1/3 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col items-center justify-center relative min-h-[350px] shrink-0">
            <h4 className="absolute top-6 left-6 font-[800] text-[#0f172a] uppercase tracking-tighter">Species Breakdown</h4>
            <div className="relative w-48 h-48 mt-8">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="96" cy="96" r="80" fill="none" stroke="#10b981" strokeWidth="20" strokeDasharray="502" strokeDashoffset="251" />
                <circle cx="96" cy="96" r="80" fill="none" stroke={accentColor} strokeWidth="20" strokeDasharray="502" strokeDashoffset="351" />
                <circle cx="96" cy="96" r="80" fill="none" stroke="#3b82f6" strokeWidth="20" strokeDasharray="502" strokeDashoffset="451" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-2xl font-black">156</span>
                <span className="text-xs text-slate-400">Total Tests</span>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              <span className="text-xs font-bold px-2 py-1 bg-[#10b981]/10 text-[#10b981] rounded-md">Cattle 50%</span>
              <span className="text-xs font-bold px-2 py-1 bg-[#f59e0b]/10 text-[#f59e0b] rounded-md">Poultry 20%</span>
              <span className="text-xs font-bold px-2 py-1 bg-[#3b82f6]/10 text-[#3b82f6] rounded-md">Equine 10%</span>
            </div>
          </div>
          <div className="flex-1 space-y-6 flex flex-col min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 shrink-0">
              <KPICard title="Critical Alerts" value="12" trend="Review" trendType="down" icon={AlertCircle} color="bg-rose-500" />
              <KPICard title="Accuracy" value="98.5%" trend="Stable" trendType="up" icon={CheckCircle} color="bg-emerald-500" />
            </div>
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex-1 min-h-[250px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Recent Results</h4>
              <div className="space-y-3">
                {[
                  { a: "Cow - Farm A", t: "Brucellosis", s: "Negative", c: "bg-emerald-100 text-emerald-700" },
                  { a: "Horse - Stable 2", t: "EIA", s: "Pending", c: "bg-amber-100 text-amber-700" },
                  { a: "Flock - Shed B", t: "AI Screening", s: "Completed", c: "bg-blue-100 text-blue-700" }
                ].map((res, i) => (
                  <div key={i} className="flex justify-between items-center p-3 hover:bg-slate-50 rounded-xl border border-transparent hover:border-slate-100 transition-colors">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{res.a}</p>
                      <p className="text-xs text-slate-500">{res.t}</p>
                    </div>
                    <span className={cn("text-xs font-bold px-3 py-1 rounded-full", res.c)}>{res.s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 11. LEAD MANAGEMENT CRM
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 11. LEAD MANAGEMENT CRM
// ---------------------------------------------------------------------------
const CRMLeadsDashboard = ({ accentColor = "#2C78B9" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Pipeline", active: true },
    { icon: Phone, label: "Calls & Activity" },
    { icon: FileText, label: "Quotations" },
    { icon: UserPlus, label: "Lead Import" },
    { icon: BarChart3, label: "Win/Loss Analysis" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={UserPlus}
        brand="Mx-CRM"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 flex flex-col h-full overflow-y-auto">
          <div className="flex justify-between items-end mb-6 shrink-0">
            <h3 className="text-2xl font-[800] text-[#0f172a]">Sales Pipeline</h3>
            <p className="text-sm font-bold text-slate-500 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">Pipeline Value: <span style={{ color: accentColor }}>₹42.5L</span></p>
          </div>
          <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-[500px]">
            {[
              { n: "New", c: 12, cards: [{ t: "Tech Corp", v: "₹2L" }, { t: "Alpha Inc", v: "₹5L" }] },
              { n: "Contacted", c: 8, cards: [{ t: "Beta LLC", v: "₹1.5L" }] },
              { n: "Proposal", c: 5, cards: [{ t: "Gamma Group", v: "₹12L" }, { t: "Delta Corp", v: "₹8L" }] },
              { n: "Won", c: 3, cards: [{ t: "Omega Ltd", v: "₹4L", w: true }], bg: "bg-emerald-50/50" }
            ].map((col, i) => (
              <div key={i} className={cn("flex-1 bg-slate-100/50 rounded-2xl p-3 flex flex-col", col.bg)}>
                <div className="flex justify-between items-center mb-4 px-1">
                  <span className="font-bold text-slate-700 text-[10px] uppercase tracking-wider">{col.n}</span>
                  <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-[10px] font-black shadow-sm text-slate-500">{col.c}</span>
                </div>
                <div className="space-y-3">
                  {col.cards.map((card: any, j) => (
                    <motion.div key={j} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 + j * 0.1 }} className={cn("bg-white p-3 rounded-xl shadow-sm border", card.w ? "border-emerald-200" : "border-slate-200")}>
                      <p className="font-bold text-slate-800 text-xs">{card.t}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-[10px] font-bold" style={{ color: accentColor }}>{card.v}</span>
                        {card.w && <span className="text-[8px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">WON</span>}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 12. SERVICE MANAGEMENT
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 12. SERVICE MANAGEMENT
// ---------------------------------------------------------------------------
const ServiceDashboard = ({ accentColor = "#059A8F" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Service Tickets", active: true },
    { icon: Users, label: "Engineers" },
    { icon: Package, label: "Spare Parts" },
    { icon: FileText, label: "AMC Contracts" },
    { icon: BarChart3, label: "SLA Reports" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Briefcase}
        brand="SERVICE DESK"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 space-y-4 flex flex-col overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 shrink-0">
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col items-center justify-center min-h-[220px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Ticket Status</h4>
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="64" cy="64" r="50" fill="none" stroke="#f1f5f9" strokeWidth="16" />
                  <circle cx="64" cy="64" r="50" fill="none" stroke="#ef4444" strokeWidth="16" strokeDasharray="314" strokeDashoffset="204" />
                  <circle cx="64" cy="64" r="50" fill="none" stroke="#f59e0b" strokeWidth="16" strokeDasharray="314" strokeDashoffset="140" />
                  <circle cx="64" cy="64" r="50" fill="none" stroke={accentColor} strokeWidth="16" strokeDasharray="314" strokeDashoffset="80" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-black">142</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm min-h-[220px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-6">SLA Tracker</h4>
              <div className="space-y-4">
                {[
                  { l: "Critical", p: 92, c: "bg-rose-500" },
                  { l: "High", p: 88, c: "bg-amber-500" },
                  { l: "Normal", p: 96, c: `bg-[${accentColor}]` }
                ].map((sla, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="w-16 text-[10px] font-bold text-slate-600 uppercase tracking-tight">{sla.l}</span>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }} whileInView={{ width: `${sla.p}%` }} className={cn("h-full", sla.c)} style={sla.l === 'Normal' ? { backgroundColor: accentColor } : {}} />
                    </div>
                    <span className="text-[10px] font-black">{sla.p}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 shrink-0">
            <div className="flex-1 p-4 bg-white rounded-xl border border-slate-200 shadow-sm flex justify-between items-center">
              <span className="text-sm font-bold text-slate-500">Avg Resolution</span>
              <span className="text-xl font-black text-slate-900">4.2 Hrs</span>
            </div>
            <div className="flex-1 p-4 bg-white rounded-xl border border-slate-200 shadow-sm flex justify-between items-center">
              <span className="text-sm font-bold text-slate-500">CSAT Score</span>
              <span className="text-xl font-black text-emerald-500">4.8 ★</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 13. SALES AUTOMATION
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 13. SALES AUTOMATION
// ---------------------------------------------------------------------------
const SalesAutoDashboard = ({ accentColor = "#2C78B9" }: WidgetProps) => {
  const sidebarItems = [
    { icon: LayoutDashboard, label: "Performance", active: true },
    { icon: Users, label: "Sequences" },
    { icon: Award, label: "Leaderboard" },
    { icon: Clock, label: "Follow-ups" },
    { icon: CheckCircle, label: "Approvals" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50">
      <DashboardSidebar
        icon={Crosshair}
        brand="SALES AUTO"
        items={sidebarItems}
        accentColor={accentColor}
        showProfile={true}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <DashboardHeader accentColor={accentColor} />
        <div className="flex-1 p-6 flex flex-col lg:flex-row gap-4 overflow-auto">
          <div className="flex-1 space-y-6 flex flex-col min-w-0">
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex-1 min-h-[300px]">
              <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-4">Monthly Revenue Trend</h4>
              <div className="h-48">
                <AnimatedAreaChart color1="#94a3b8" color2={accentColor} />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <div className="flex-1 p-4 bg-slate-100 rounded-xl border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">This Month</p>
                <p className="text-2xl font-black text-[#0f172a]">₹2.4Cr</p>
              </div>
              <div className="flex-1 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Target Achieved</p>
                <p className="text-2xl font-black text-emerald-700">186%</p>
              </div>
            </div>
          </div>
          <div className="w-full lg:w-64 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm shrink-0">
            <h4 className="font-[800] text-[#0f172a] uppercase tracking-tighter mb-6">Leaderboard</h4>
            <div className="space-y-6">
              {[
                { r: 1, n: "Rahul S.", v: "₹82L", p: 95 },
                { r: 2, n: "Priya M.", v: "₹65L", p: 75 },
                { r: 3, n: "Amit K.", v: "₹42L", p: 50 }
              ].map((rep, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-500">{rep.r}</div>
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-sm font-bold text-slate-800">{rep.n}</span>
                      <span className="text-xs font-bold text-slate-500">{rep.v}</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full pl-9">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${rep.p}%` }} className="h-full rounded-full" style={{ backgroundColor: i === 0 ? accentColor : '#94a3b8' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN EXPORT
// ---------------------------------------------------------------------------

export default function DashboardWidget({ type, accentColor = "#2C78B9" }: { type: string, accentColor?: string }) {
  switch (type) {
    case 'construction': return <ConstructionDashboard accentColor={accentColor} />;
    case 'traders': return <TradingDashboard accentColor={accentColor} />;
    case 'distributors': return <DistributorDashboard accentColor={accentColor} />;
    case 'retail': return <RetailDashboard accentColor={accentColor} />;
    case 'buying-house': return <BuyingHouseDashboard accentColor={accentColor} />;
    case 'ecommerce': return <EcommerceDashboard accentColor={accentColor} />;
    case 'hospital': return <HospitalDashboard accentColor={accentColor} />;
    case 'lab': return <LabDashboard accentColor={accentColor} />;
    case 'ris': return <RisDashboard accentColor={accentColor} />;
    case 'pharmacy': return <PharmacyDashboard accentColor={accentColor} />;
    case 'blood-bank': return <BloodBankDashboard accentColor={accentColor} />;
    case 'queue': return <QueueDashboard accentColor={accentColor} />;
    case 'veterinary': return <VetDashboard accentColor={accentColor} />;
    case 'lead-management': return <CRMLeadsDashboard accentColor={accentColor} />;
    case 'service': return <ServiceDashboard accentColor={accentColor} />;
    case 'sales-auto': return <SalesAutoDashboard accentColor={accentColor} />;
    default: return <ManufacturingDashboard accentColor={accentColor} />;
  }
}
